import React from 'react';
import { MessageSquare } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * Renders the primary source's content with all overlays:
 *   - Highlighted text spans (via renderHighlightedContent)
 *   - Drawing canvas overlay
 *   - Margin-note bubbles down the right gutter
 *   - Inline analysis questions + teacher feedback panels
 *
 * Extracted from AnnotationTool to keep that file under control.
 */
export default function SourceViewer({
  source,
  activeTool,
  showDrawingCanvas,
  canvasRef,
  marginNotes,
  teacherFeedback,
  renderHighlightedContent,
  onTextSelection,
  onAddMarginNote,
  onOpenNote,
  onStartDrawing,
  onDraw,
  onStopDrawing,
}) {
  return (
    <div className="lg:col-span-3">
      <Card className="p-6 relative">
        <div className="mb-4">
          <h2 className="font-serif-display text-2xl text-[#14213D] mb-2">
            {source?.title}
          </h2>
          <div className="flex items-center gap-2 text-sm text-[#5A6577]">
            <Badge variant="outline">{source?.type}</Badge>
            {source?.date_created && <span>{source.date_created}</span>}
            {source?.source_organization && (
              <span>&middot; {source.source_organization}</span>
            )}
          </div>
        </div>

        {/* Source Content with Annotations */}
        <div
          id="source-content"
          className="prose prose-stone max-w-none relative"
          onMouseUp={onTextSelection}
          onClick={activeTool === 'note' ? onAddMarginNote : undefined}
          style={{ cursor: activeTool === 'note' ? 'crosshair' : 'text' }}
        >
          <div className="text-[#2D3748] leading-relaxed">
            {renderHighlightedContent()}
          </div>

          {showDrawingCanvas && (
            <canvas
              ref={canvasRef}
              width={800}
              height={600}
              className="absolute top-0 left-0 w-full h-full pointer-events-auto"
              style={{ cursor: 'crosshair' }}
              onMouseDown={onStartDrawing}
              onMouseMove={onDraw}
              onMouseUp={onStopDrawing}
              onMouseLeave={onStopDrawing}
            />
          )}

          {/* Margin-note bubbles down the right gutter */}
          <div className="absolute right-0 top-0 bottom-0 w-8">
            {marginNotes.map((note) => (
              <div
                key={note.id}
                className="absolute right-0 w-6 h-6 bg-[#D4AF37] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#C19E2C] transition-colors"
                style={{ top: `${note.position_y}%` }}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenNote(note);
                }}
                title={note.content}
              >
                <MessageSquare className="h-3 w-3 text-[#14213D]" />
              </div>
            ))}
          </div>
        </div>

        {source?.analysis_questions && source.analysis_questions.length > 0 && (
          <div className="mt-6 p-4 bg-[#FBF6E8] border border-[#D4AF37]/40 rounded-lg">
            <h3 className="text-sm font-semibold text-[#14213D] mb-2">Analysis Questions:</h3>
            <ul className="text-sm text-[#2D3748] space-y-2">
              {source.analysis_questions.map((q, idx) => (
                <li key={`q-${idx}-${(q || '').slice(0, 24)}`} className="flex gap-2">
                  <span className="text-[#D4AF37] font-medium">{idx + 1}.</span>
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {teacherFeedback && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">Teacher Feedback:</h3>
            <p className="text-sm text-blue-800">{teacherFeedback}</p>
          </div>
        )}
      </Card>
    </div>
  );
}
